import { createElement, useState, useEffect, useRef } from "react";
import { Link, router } from "@inertiajs/react";
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PlayCircleIcon,
  EyeIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { AdminLayout } from "../../../layouts/admin-layout";

interface Deploy {
  id: number;
  deploy_type: string;
  status: string;
  is_live: boolean;
  created_at: string | null;
  finished_at: string | null;
  project: { id: number; name: string | null } | null;
  account: { id: number; name: string | null } | null;
  owner_email: string | null;
  trace_url: string;
}

interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
  prev_page: number | null;
  next_page: number | null;
}

interface Filters {
  status: string;
  email: string;
}

interface DeploysIndexProps {
  deploys: Deploy[];
  pagination: Pagination;
  filters: Filters;
  status_options: string[];
}

const STATUS_LABELS: Record<string, string> = {
  all: "All",
  success: "Success",
  failure: "Failure",
  in_progress: "In progress",
  cancelled: "Cancelled",
};

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string; Icon: React.ElementType }> = {
    completed: { bg: "bg-green-100", text: "text-green-800", Icon: CheckCircleIcon },
    failed: { bg: "bg-red-100", text: "text-red-800", Icon: XCircleIcon },
    cancelled: { bg: "bg-muted", text: "text-muted-foreground", Icon: XCircleIcon },
    pending: { bg: "bg-amber-100", text: "text-amber-800", Icon: ClockIcon },
    running: { bg: "bg-blue-100", text: "text-blue-800", Icon: PlayCircleIcon },
  };
  const c = config[status] || { bg: "bg-muted", text: "text-muted-foreground", Icon: ClockIcon };
  const Icon = c.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium capitalize ${c.bg} ${c.text}`}
    >
      <Icon className="w-3 h-3" />
      {status}
    </span>
  );
}

function navigateWithParams(updates: Record<string, string | null>) {
  const url = new URL(window.location.href);
  Object.entries(updates).forEach(([key, value]) => {
    if (value == null || value === "") {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, value);
    }
  });
  router.visit(url.pathname + url.search, {
    preserveState: true,
    preserveScroll: true,
  });
}

function FilterBar({ filters, status_options }: { filters: Filters; status_options: string[] }) {
  const [emailDraft, setEmailDraft] = useState(filters.email);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset draft if the URL-driven prop changes from elsewhere (e.g. clearing).
  useEffect(() => {
    setEmailDraft(filters.email);
  }, [filters.email]);

  const handleEmailChange = (value: string) => {
    setEmailDraft(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      // Reset to page 1 whenever search changes.
      navigateWithParams({ email: value, page: null });
    }, 300);
  };

  return (
    <div className="flex items-center gap-3 flex-wrap mb-4">
      <div className="flex items-center gap-1 bg-card border border-border rounded-md p-1">
        {status_options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => navigateWithParams({ status: opt, page: null })}
            className={`px-3 py-1 text-sm rounded transition-colors ${
              filters.status === opt
                ? "bg-primary text-primary-foreground"
                : "text-foreground hover:bg-muted"
            }`}
          >
            {STATUS_LABELS[opt] || opt}
          </button>
        ))}
      </div>

      <div className="relative flex-1 max-w-sm">
        <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="search"
          placeholder="Search by owner email…"
          value={emailDraft}
          onChange={(e) => handleEmailChange(e.target.value)}
          className="w-full pl-8 pr-3 py-1.5 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
      </div>
    </div>
  );
}

function PaginationControls({ pagination }: { pagination: Pagination }) {
  if (pagination.total_pages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-border">
      <span className="text-sm text-muted-foreground">
        Page {pagination.current_page} of {pagination.total_pages} ({pagination.total_count} total)
      </span>
      <div className="flex gap-2">
        <button
          onClick={() => navigateWithParams({ page: String(pagination.current_page - 1) })}
          disabled={!pagination.prev_page}
          className="px-3 py-1 text-sm rounded-md border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <button
          onClick={() => navigateWithParams({ page: String(pagination.current_page + 1) })}
          disabled={!pagination.next_page}
          className="px-3 py-1 text-sm rounded-md border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function DeploysIndex({ deploys, pagination, filters, status_options }: DeploysIndexProps) {
  return (
    <div className="p-6 max-w-7xl mx-auto">
      <header className="mb-6">
        <h1 className="text-3xl font-bold text-foreground">Deploys</h1>
        <p className="text-muted-foreground mt-1">
          Cross-account deploy log with Google Ads trace for each entry.
        </p>
      </header>

      <FilterBar filters={filters} status_options={status_options} />

      <div className="bg-card rounded-lg border border-border shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">ID</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Type</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Status</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Project</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Owner email</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Created</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Finished</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Trace</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {deploys.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No deploys match the current filters.
                </td>
              </tr>
            ) : (
              deploys.map((deploy) => (
                <tr key={deploy.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 text-sm font-mono text-foreground">#{deploy.id}</td>
                  <td className="px-4 py-3 text-sm text-foreground capitalize">{deploy.deploy_type}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={deploy.status} />
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">
                    {deploy.project ? deploy.project.name || `Project #${deploy.project.id}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {deploy.owner_email || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {formatDateTime(deploy.created_at)}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {formatDateTime(deploy.finished_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      href={deploy.trace_url}
                      className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80"
                    >
                      <EyeIcon className="w-4 h-4" />
                      View
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <PaginationControls pagination={pagination} />
      </div>
    </div>
  );
}

DeploysIndex.layout = (page: React.ReactNode) => createElement(AdminLayout, null, page);

export default DeploysIndex;
